/**
 * Recently Viewed Products
 *
 * Tracks product handles in localStorage and renders them on the
 * <recently-viewed> custom element using Shopify's section rendering API.
 *
 * Usage:
 *   1. On a product page, this script writes the current product handle to localStorage.
 *   2. On any page with <recently-viewed data-current-handle="..."></recently-viewed>,
 *      this script fetches each previously-viewed product's card markup from a section
 *      and injects it inside the element.
 *
 * Storage key: evac_recent (JSON array, max 8 entries, deduped, MRU first)
 */

(function () {
  const STORAGE_KEY = 'evac_recent';
  const MAX_ITEMS = 8;
  const SECTION_ID = 'recently-viewed-card';

  function readList() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function writeList(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_ITEMS)));
    } catch (e) {
      // localStorage may be full or disabled — silently ignore
    }
  }

  function trackCurrent() {
    // Only track on actual product pages — read the handle from a meta tag
    // injected by the snippet, OR fall back to parsing the URL.
    const meta = document.querySelector('meta[name="evac:product-handle"]');
    let handle = meta && meta.content;
    if (!handle) {
      const m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
      if (m) handle = m[1];
    }
    if (!handle) return;
    const list = readList().filter(h => h !== handle);
    list.unshift(handle);
    writeList(list);
  }

  async function renderInto(container) {
    const currentHandle = container.dataset.currentHandle || '';
    const handles = readList().filter(h => h !== currentHandle).slice(0, MAX_ITEMS);
    if (handles.length === 0) {
      container.style.display = 'none';
      return;
    }

    const grid = container.querySelector('.recently-viewed__grid');
    if (!grid) {
      container.style.display = 'none';
      return;
    }

    const fetches = handles.map(async (handle) => {
      try {
        const res = await fetch(`/products/${encodeURIComponent(handle)}?section_id=${SECTION_ID}`);
        if (!res.ok) return { handle, card: null };
        const html = await res.text();
        const wrap = document.createElement('div');
        wrap.innerHTML = html;
        // The section template wraps in a shopify-section div — pull only the inner card
        const card = wrap.querySelector('[data-recently-viewed-card]');
        // A deleted product's URL returns 200 with a blank product: the
        // marker comes back empty (or, from stale caches, holding the
        // onboarding placeholder card). Treat both as dead.
        if (!card || !card.firstElementChild || card.querySelector('.card__heading--placeholder')) {
          return { handle, card: null };
        }
        return { handle, card };
      } catch (e) {
        return { handle, card: null };
      }
    });

    const results = await Promise.all(fetches);
    const cards = results.filter(r => r.card).map(r => r.card);
    // prune dead handles so they stop being fetched on every page view
    const dead = results.filter(r => !r.card).map(r => r.handle);
    if (dead.length > 0) {
      writeList(readList().filter(h => dead.indexOf(h) === -1));
    }
    if (cards.length === 0) {
      container.style.display = 'none';
      return;
    }

    grid.innerHTML = '';
    cards.forEach(c => grid.appendChild(c));
    container.removeAttribute('hidden');
  }

  // 1. Track current product
  if (window.location.pathname.indexOf('/products/') === 0 || window.location.pathname.includes('/products/')) {
    trackCurrent();
  }

  // 2. Render any <recently-viewed> elements on the page
  function init() {
    const containers = document.querySelectorAll('recently-viewed');
    containers.forEach(c => renderInto(c));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
